import React, {useCallback, useEffect, useState} from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import {useNavigate} from "react-router-dom";
import Alert, {AlertProps} from "../../common/alert";
import {SubmitHandler, useForm} from "react-hook-form";
import {INDUSTRY_TYPES, IndustryType, VoiceTrainingScriptDTO} from "@src/model/voice_training_script";
import {useVoiceTrainingScriptAPI} from "@src/api/voice_training_script_api";
import {useOrganizationAPI} from "@src/api/organization.api";
import {Organization} from "@src/model/organization";

const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Never";
    try {
        return new Date(dateString).toLocaleString();
    } catch (e) {
        return "Invalid Date";
    }
};

const VoiceTrainingScriptIndex: React.FC = () => {
    const [data, setData] = useState<VoiceTrainingScriptDTO[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const {getAllScripts, createScript, deleteScript} = useVoiceTrainingScriptAPI();
    const {getAllOrganizations} = useOrganizationAPI();
    const navigate = useNavigate();

    const {register, handleSubmit, reset, formState: {errors}} = useForm<VoiceTrainingScriptDTO>();

    const fetchData = useCallback(async () => {
        setLoading(true);
        setStatus(null);
        try {
            const [scripts, orgs] = await Promise.all([
                getAllScripts(),
                getAllOrganizations(),
            ]);
            setData(scripts);
            setOrganizations(orgs.filter(org => org.organization_type === "agency"));
        } catch (error: any) {
            setStatus({type: "error", message: "Failed to load data: " + error.message});
        } finally {
            setLoading(false);
        }
    }, [getAllScripts, getAllOrganizations]);

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (id: string | undefined) => {
        if (id) {
            navigate(`/voice-training-scripts/edit/${id}`);
        }
    };

    const handleDelete = (id: string | undefined) => {
        if (id) {
            if (window.confirm("Are you sure you want to delete this script?")) {
                setActionLoading(id);
                deleteScript(id)
                    .then(() => {
                        setStatus({type: "success", message: "Script deleted successfully."});
                        fetchData();
                    })
                    .catch((error: any) => {
                        setStatus({type: "error", message: "Failed to delete script: " + error.message});
                    })
                    .finally(() => setActionLoading(null));
            }
        }
    };

    const onCreateSubmit: SubmitHandler<VoiceTrainingScriptDTO> = async (formData) => {
        setStatus(null);
        setLoading(true);
        try {
            // Clean up empty strings
            const cleanedData = {
                ...formData,
                industry_type: formData.industry_type || undefined,
                organization_id: formData.organization_id || undefined,
            };
            await createScript(cleanedData);
            setStatus({type: "success", message: "Script created successfully."});
            reset();
            fetchData();
        } catch (error: any) {
            setStatus({type: "error", message: "Failed to create script: " + error.message});
            setLoading(false);
        }
    };

    const getOrganizationName = (orgId: string | undefined) => {
        if (!orgId) return "—";
        const org = organizations.find(o => o.id === orgId);
        return org ? org.name : orgId;
    };

    const getIndustryLabel = (industryType: IndustryType | undefined) => {
        if (!industryType) return "—";
        const industry = INDUSTRY_TYPES.find(i => i.value === industryType);
        return industry ? industry.label : industryType;
    };

    const columns = [
        {
            name: "Title",
            selector: (row: VoiceTrainingScriptDTO) => row.title || '',
            sortable: true,
            grow: 2,
        },
        {
            name: "Industry Type",
            selector: (row: VoiceTrainingScriptDTO) => row.industry_type || '',
            sortable: true,
            cell: (row: VoiceTrainingScriptDTO) => (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    row.industry_type
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-gray-100 text-gray-500"
                }`}>
          {getIndustryLabel(row.industry_type)}
        </span>
            ),
        },
        {
            name: "Organization",
            selector: (row: VoiceTrainingScriptDTO) => row.organization_id || '',
            sortable: true,
            cell: (row: VoiceTrainingScriptDTO) => (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    row.organization_id
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-gray-100 text-gray-500"
                }`}>
          {getOrganizationName(row.organization_id)}
        </span>
            ),
        },
        {
            name: "Language",
            selector: (row: VoiceTrainingScriptDTO) => row.language || 'en',
            sortable: true,
            width: "100px",
        },
        {
            name: "Updated At",
            selector: (row: VoiceTrainingScriptDTO) => formatDate(row.updated_at),
            sortable: true,
        },
        {
            name: "Actions",
            button: true,
            cell: (row: VoiceTrainingScriptDTO) => (
                <div className="flex space-x-1">
                    <button
                        onClick={() => handleEdit(row.id)}
                        disabled={!!actionLoading}
                        className="p-1 bg-blue-500 text-white rounded hover:bg-blue-700 text-xs disabled:opacity-50"
                        aria-label="Edit"
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        disabled={!!actionLoading}
                        className="p-1 bg-red-500 text-white rounded hover:bg-red-700 text-xs disabled:opacity-50"
                        aria-label="Delete"
                    >
                        {actionLoading === row.id ? "..." : "Delete"}
                    </button>
                </div>
            ),
        },
    ];

    return (
        <AppContainer>
            <div className="p-6">
                <h1 className="text-2xl font-semibold mb-1">Voice Training Scripts</h1>
                <p className="text-sm text-gray-600 mb-4">
                    Manage voice training scripts for different industries and organizations.
                </p>

                {status && <Alert {...status} onClose={() => setStatus(null)} className="mb-4"/>}

                <form onSubmit={handleSubmit(onCreateSubmit)} className="bg-white p-4 rounded shadow border mb-6">
                    <h2 className="text-lg font-semibold mb-3">Create New Script</h2>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                                    Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    {...register("title", {required: "Title is required"})}
                                    className={`block w-full p-2 border rounded shadow-sm text-sm ${
                                        errors.title ? "border-red-500" : "border-gray-300"
                                    }`}
                                    placeholder="Enter script title"
                                />
                                {errors.title && (
                                    <p className="text-red-500 text-xs mt-1">{String(errors.title.message)}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                                    Language
                                </label>
                                <input
                                    type="text"
                                    id="language"
                                    {...register("language")}
                                    className="block w-full p-2 border rounded shadow-sm text-sm border-gray-300"
                                    placeholder="en"
                                    defaultValue="en"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="industry_type" className="block text-sm font-medium text-gray-700 mb-1">
                                    Industry Type
                                </label>
                                <select
                                    id="industry_type"
                                    {...register("industry_type")}
                                    className="block w-full p-2 border rounded shadow-sm text-sm border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">— No Industry (Public) —</option>
                                    {INDUSTRY_TYPES.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="organization_id"
                                       className="block text-sm font-medium text-gray-700 mb-1">
                                    Organization
                                </label>
                                <select
                                    id="organization_id"
                                    {...register("organization_id")}
                                    className="block w-full p-2 border rounded shadow-sm text-sm border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">— No Organization (Public) —</option>
                                    {organizations.map((org) => (
                                        <option key={org.id} value={org.id}>
                                            {org.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                                Script Content <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="content"
                                {...register("content", {required: "Content is required"})}
                                className={`block w-full p-2 border rounded shadow-sm text-sm ${
                                    errors.content ? "border-red-500" : "border-gray-300"
                                }`}
                                placeholder="Enter the voice training script content..."
                                rows={6}
                            />
                            {errors.content && (
                                <p className="text-red-500 text-xs mt-1">{String(errors.content.message)}</p>
                            )}
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 text-sm font-medium"
                            >
                                + Create Script
                            </button>
                        </div>
                    </div>
                </form>

                <div className="border rounded shadow-sm">
                    <DataTable
                        columns={columns}
                        data={data}
                        progressPending={loading}
                        progressComponent={<div className="py-10 text-center">Loading...</div>}
                        noDataComponent={
                            <div className="py-10 text-center text-gray-500">No voice training scripts found.</div>
                        }
                        pagination
                        highlightOnHover
                        striped
                        customStyles={{
                            headCells: {
                                style: {
                                    fontSize: "13px",
                                    fontWeight: "bold",
                                    color: "#4a5568",
                                    backgroundColor: "#f7fafc",
                                },
                            },
                            cells: {style: {fontSize: "13px"}},
                        }}
                    />
                </div>
            </div>
        </AppContainer>
    );
};

export default VoiceTrainingScriptIndex;

